import { MessageSquare, Wand2, RefreshCw, Rocket } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect, useRef } from "react";

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  const steps = [
    {
      number: "1",
      day: t('howItWorks.step1.day'),
      icon: MessageSquare,
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.desc')
    },
    {
      number: "2", 
      day: t('howItWorks.step2.day'),
      icon: Wand2,
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.desc')
    },
    {
      number: "3",
      day: t('howItWorks.step3.day'),
      icon: RefreshCw,
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.desc')
    },
    {
      number: "4",
      day: t('howItWorks.step4.day'),
      icon: Rocket,
      title: t('howItWorks.step4.title'),
      description: t('howItWorks.step4.desc')
    }
  ];

  useEffect(() => {
    const observers = stepRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleSteps((prev) => [...new Set([...prev, index])]);
              }, index * 200);
            }
          });
        },
        { threshold: 0.2 }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <section id="how-it-works" className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-outline text-primary mb-4 md:mb-6 uppercase px-2" style={{ fontWeight: 900 }}>
            {t('howItWorks.title').split(' ').slice(0, 2).join(' ')}{' '}
            <span className="relative inline-block">
              <span className="relative z-10">{t('howItWorks.title').split(' ')[2]}</span>
              <span 
                className="absolute bottom-2 left-0 right-0 h-4 -rotate-1 -z-0"
                style={{
                  backgroundColor: 'hsl(11, 86%, 55%)',
                  background: 'linear-gradient(104deg, rgba(239, 106, 55, 0) 0.9%, rgba(239, 106, 55, 1) 2.4%, rgba(239, 106, 55, 0.6) 5.8%, rgba(239, 106, 55, 0.1) 93%, rgba(239, 106, 55, 0.8) 96%, rgba(239, 106, 55, 0) 98%), linear-gradient(183deg, rgba(239, 106, 55, 0) 0%, rgba(239, 106, 55, 0.3) 7.9%, rgba(239, 106, 55, 0) 15%)'
                }}
              />
            </span>
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-medium text-center px-2">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        <div className="relative" ref={sectionRef}>
          {/* Vertical Timeline - animated progressively */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-border/30 hidden lg:block transform -translate-x-1/2 overflow-hidden">
            <div 
              className="absolute top-0 left-0 right-0 bg-gradient-to-b from-accent via-accent/90 to-accent/70 transition-all duration-1000 ease-out origin-top"
              style={{ 
                height: visibleSteps.length > 0 
                  ? `${Math.min(((Math.max(...visibleSteps) + 1) / steps.length) * 100, 100)}%` 
                  : '0%'
              }}
            >
              {/* Glow effect at the end of the line */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-accent rounded-full blur-sm"></div>
            </div>
          </div>
          
          <div className="space-y-8 md:space-y-12 lg:space-y-24">
            {steps.map((step, index) => (
              <div 
                key={step.number} 
                ref={(el) => (stepRefs.current[index] = el)}
                className={`relative flex flex-col lg:flex-row items-center gap-8 transition-all duration-700 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } ${
                  visibleSteps.includes(index) 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
              >
                {/* Timeline dot - animated without pulse */}
                <div className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full border-8 border-background shadow-lg z-10 items-center justify-center transition-all duration-700 ease-out ${
                  visibleSteps.includes(index) 
                    ? 'bg-accent scale-100 shadow-accent/50' 
                    : 'bg-border/50 scale-0'
                }`}>
                  <span className={`text-white font-bold text-xl transition-opacity duration-300 delay-200 ${
                    visibleSteps.includes(index) ? 'opacity-100' : 'opacity-0'
                  }`}>{step.number}</span>
                </div>

                {/* Content card */}
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className={`bg-card rounded-2xl p-5 md:p-6 lg:p-8 shadow-elegant border border-border/50 group transition-all duration-700 ${
                    visibleSteps.includes(index)
                      ? 'hover:shadow-accent hover:scale-105 hover:-translate-y-2'
                      : ''
                  }`}>
                    {/* Day badge */}
                    <div className={`inline-flex items-center justify-center px-4 py-2 bg-accent/10 rounded-full mb-4 ${index % 2 === 0 ? 'lg:float-right lg:ml-4' : 'lg:float-left lg:mr-4'}`}>
                      <span className="text-accent font-bold text-sm">{step.day}</span>
                    </div>

                    {/* Mobile step number */}
                    <div className="lg:hidden w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto">
                      {step.number}
                    </div>

                    {/* Icon */}
                    <div className={`mb-6 flex ${index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'} justify-center`}>
                      <div className={`w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center group-hover:bg-accent/20 transition-all duration-300 ${
                        visibleSteps.includes(index) ? 'group-hover:scale-110 group-hover:rotate-6' : ''
                      }`}>
                        <step.icon className="w-8 h-8 text-accent" />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-display font-bold text-card-foreground mb-4 uppercase">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-lg">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Empty space for alignment */}
                <div className="hidden lg:block w-5/12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;